import {
	ReactElement,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { defaultTileGridConfiguration, TileGridConfiguration } from "./RenderState/common";
import { deepComparison } from "client/Misc/compareUtils";
import { debounce } from "client/Misc/utils";
import styles from "./TileGridConfigurationTracker.module.less";

export type TileGridConfigurationTrackerProps = {
	onNewConfiguration: (tileGridConfiguration: TileGridConfiguration) => void;
};

export const TileGridConfigurationTracker = (props: TileGridConfigurationTrackerProps): ReactElement => {
	const {
		onNewConfiguration
	} = props;

	const ref: React.RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	useLayoutEffect(
		(): (() => void) | void => {
			const element = ref.current;
			if (!element) {
				onNewConfiguration(defaultTileGridConfiguration);
				return;
			}

			const cancellation = new AbortController();

			const loadFromDom = (): void => {
				const { width, marginLeft } = window.getComputedStyle(element);
				const columns: number = Math.round(parseFloat(width));
				const renderLiveTilesFullWidth: boolean = Math.round(parseFloat(marginLeft)) > 0;

				const value: TileGridConfiguration = {
					dimensionLimits: {
						minRows: 1,
						columns
					},
					renderLiveTilesFullWidth
				};

				onNewConfiguration(value);
			};

			element.addEventListener(
				"transitionend",
				debounce(loadFromDom, 200),
				{ signal: cancellation.signal }
			);

			// do the initial setting of the configuration manually
			loadFromDom();

			return (): void => cancellation.abort();
		},
		[onNewConfiguration]
	);

	return (
		<div
			className={styles.root}
			ref={ref}
		/>
	);
};
TileGridConfigurationTracker.displayName = "TileGridConfigurationTracker";

export function useTileGridConfigurationState(): [
	configuration: TileGridConfiguration,
	onChange: (newConfiguration: TileGridConfiguration) => void
] {
	const [state, setState] = useState<TileGridConfiguration>(defaultTileGridConfiguration);

	const changeHandler = useCallback(
		(newConfiguration: TileGridConfiguration): void => {
			setState(
				(x): TileGridConfiguration => {
					if (deepComparison(x, newConfiguration)) {
						return x;
					}

					return newConfiguration;
				}
			);
		},
		[]
	);

	return useMemo(
		(): [TileGridConfiguration, (newConfiguration: TileGridConfiguration) => void] => [state, changeHandler],
		[state, changeHandler]
	);
}